import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Root, Value, Portal, ItemText } from '@radix-ui/react-select';
import { ChevronDownIcon } from '@radix-ui/react-icons';

import PeopleIcon from 'assets/icon-user';
import {
  SelectRoot,
  SelectTrigger,
  TriggerContent,
  SelectIcon,
  SelectContent,
  SelectViewport,
  SelectItem,
} from './SelectWrapper.styles';

interface OptionType {
  label: string | number;
  value: string | number;
  className?: string;
}

interface SelectWrapperProps {
  options: OptionType[];
  value?: OptionType | string | number;
  defaultValue?: OptionType | string | number;
  onChange?: (event: { target: { value: OptionType } }) => void;
  disabled?: boolean;
  placeholder?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
}

const SelectWrapper = ({
  options,
  value,
  defaultValue,
  onChange,
  disabled,
  placeholder,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  onClick,
}: SelectWrapperProps) => {
  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (document.body.hasAttribute('data-scroll-locked')) {
        document.body.removeAttribute('data-scroll-locked');
      }
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-scroll-locked', 'style'],
    });

    return () => observer.disconnect();
  }, []);
  const { t: translate } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  const currentValue: string | number | undefined = useMemo(() => {
    if (typeof value === 'object' && value?.value !== undefined) {
      return value.value;
    }
    if (value !== undefined && typeof value !== 'object') {
      return value;
    }
    if (typeof defaultValue === 'object' && defaultValue?.value !== undefined) {
      return defaultValue.value;
    }
    if (defaultValue !== undefined && typeof defaultValue !== 'object') {
      return defaultValue;
    }
    return undefined;
  }, [value, defaultValue]);

  const handleValueChange = useCallback(
    (selectedValue: string) => {
      const selectedOption = options.find(
        (option) => option.value.toString() === selectedValue,
      );
      if (selectedOption && onChange) {
        const syntheticEvent = {
          target: { value: selectedOption },
          type: 'change',
        };
        onChange(syntheticEvent);
      }
    },
    [onChange, options],
  );

  return (
    <SelectRoot ref={containerRef} onClick={onClick}>
      <Root
        value={currentValue?.toString()}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        <SelectTrigger
          aria-label={ariaLabel || translate('selectParticipants')}
          aria-describedby={ariaDescribedBy}
        >
          <TriggerContent>
            <PeopleIcon data-testid="people-icon" style={{ marginRight: 8 }} />
            <Value placeholder={placeholder} />
          </TriggerContent>
          <SelectIcon>
            <ChevronDownIcon />
          </SelectIcon>
        </SelectTrigger>
        <Portal container={containerRef.current || undefined}>
          <SelectContent position="popper" align="start">
            <SelectViewport>
              {options.map((option, i) => (
                <SelectItem
                  key={option.value}
                  value={option.value.toString()}
                  id={`react-select-2-option-${i}`}
                >
                  <ItemText>{option.label}</ItemText>
                </SelectItem>
              ))}
            </SelectViewport>
          </SelectContent>
        </Portal>
      </Root>
    </SelectRoot>
  );
};

export default SelectWrapper;
